/**
 * CSS Style Generation
 * 
 * Simplified atomic CSS approach - no container queries, no JS width calculations.
 * Uses CSS Flexbox for all layout logic.
 */

import type { Platform, LayoutMode, BlockShareConfig, ButtonShape } from '../types';
import EMBEDDED_SPRITE from '../asset/all.svg';
import { platforms as allPlatformsMap } from '../platforms';

export interface StyleConfig {
  iconSpritePath: string;
  iconSize: number;
  buttonOpacity: number;
  buttonMargin: number;
  buttonPadding: number;
  zIndex: number;
  autoWidthThreshold: number;
  mode?: LayoutMode;
  shape?: ButtonShape;
  blockConfig?: BlockShareConfig;
}

/** Convert opacity (0-1) to hex string */
function opacityToHex(opacity: number): string {
  return Math.round(opacity * 255).toString(16).padStart(2, '0');
}

/** Check if a color string is a CSS gradient */
function isGradient(color: string): boolean {
  return color.startsWith('linear-') || color.startsWith('radial-');
}

/** Generate button CSS for a platform */
function generateButtonCSS(platform: Platform, config: StyleConfig): string {
  const iconOffset = platform.iconIndex * config.iconSize;
  
  // Handle gradients vs hex colors differently
  let bgColor: string;
  let bgColorHover: string;
  
  if (isGradient(platform.color)) {
    bgColor = platform.color;
    bgColorHover = platform.color;
  } else {
    const opacityHex = opacityToHex(config.buttonOpacity);
    bgColor = `${platform.color}${opacityHex}`;
    bgColorHover = platform.color;
  }
  
  // Skip icon styling for platforms without sprite icons
  let iconCSS = '';
  if (platform.iconIndex >= 0) {
    // Base icon position (for non-block modes)
    iconCSS = `
  .social-share-btn.social-share-btn-${platform.id} .social-share-icon {
    background-position: 0 -${iconOffset}px;
  }`;
    
    // Block mode: add vertical centering offset
    // (height - iconSize) / 2 is added to center the icon vertically
    if (config.blockConfig?.height && config.blockConfig.height > config.iconSize) {
      const verticalOffset = (config.blockConfig.height - config.iconSize) / 2;
      const adjustedY = verticalOffset - iconOffset;
      iconCSS += `
  .social-share-bar--block .social-share-btn.social-share-btn-${platform.id} .social-share-icon {
    background-position-y: ${adjustedY}px;
  }`;
    }
  }

  return `
  .social-share-btn.social-share-btn-${platform.id} {
    background: ${bgColor};
  }
  .social-share-btn.social-share-btn-${platform.id}:hover {
    background: ${bgColorHover};
  }${iconCSS}`;
}

/** Determine the background image source for icons */
function getBgImageSource(iconSpritePath: string): string {
  const isDefaultPath = !iconSpritePath || iconSpritePath.indexOf('.svg') > -1;
  return isDefaultPath ? EMBEDDED_SPRITE : iconSpritePath;
}

/** Generate block mode CSS */
// old generateBlockModeCSS
// function generateBlockModeCSS(config: StyleConfig): string {
//   const blockConfig = config.blockConfig || {};
//   const gap = blockConfig.gap ?? 1;
//   const height = blockConfig.height ?? 40;
//   const iconSize = config.iconSize;

//   return `
// /* ===========================================
//    Layout Mode: Block (Text + Icon)
//    =========================================== */
// .social-share-bar--block {
//   display: flex;
//   width: 100%;
//   gap: ${gap}px;
//   border-radius: var(--ss-radius);
//   overflow: hidden;
// }

// /* Block mode button base styles */
// .social-share-bar--block .social-share-btn,
// .social-share-bar--block a.social-share-btn,
// .social-share-bar--block button.social-share-btn {
//   height: ${height}px;
//   border-radius: 0;
//   padding: 0; /* Reset padding, layout is handled by flex */
//   width: auto;
//   min-width: auto;
//   flex: 1 1 auto;
//   margin: 0;
//   border: 0 none;
//   outline: 0 none;
//   box-shadow: none;
//   text-decoration: none;
//   justify-content: flex-start; /* Align icon to left */
//   position: relative;
// }

// /* First/last button radius */
// .social-share-bar--block .social-share-btn:first-child {
//   border-radius: var(--ss-radius) 0 0 var(--ss-radius);
// }
// .social-share-bar--block .social-share-btn:last-child {
//   border-radius: 0 var(--ss-radius) var(--ss-radius) 0;
// }
// .social-share-bar--block .social-share-btn:only-child {
//   border-radius: var(--ss-radius);
// }

// /* Fixed Square Icon Area */
// .social-share-bar--block .social-share-icon {
//   width: ${height}px; /* Make it a square */
//   height: ${height}px;
//   background-position: center; 
//   display: flex;
//   align-items: center;
//   justify-content: center;
//   flex-shrink: 0;
//   z-index: 2; 
// }

// /* Label Centered in Remaining Space */
// .social-share-label {
//   flex-grow: 1;
//   text-align: center;
//   white-space: nowrap;
//   overflow: hidden;
//   text-overflow: ellipsis;
//   font-size: 14px;
//   font-weight: bold;
//   color: #fff;
//   line-height: ${height}px;
//   /* Visual balance: padding right to offset the icon width on the left */
//   padding-right: ${config.iconSize < height ? (height - config.iconSize)/2 : 0}px;
// }

// /* Icon-Only Mode (P2 / Narrow State) */
// .social-share-bar--block .social-share-btn[data-label-state="never"] {
//   flex-grow: 0;
//   flex-basis: auto;
//   width: ${height}px; /* Fixed width equals height */
//   justify-content: center;
// }

// .social-share-bar--block .social-share-btn[data-label-state="never"] .social-share-label {
//   display: none;
// }

// /* Tooltip Positioning */
// .social-share-bar--block .social-share-tooltip {
//   bottom: calc(100% + 8px);
//   left: 50%;
//   transform: translateX(-50%);
// }
// `;
// }


// 修改 src/ui/styles.ts

function generateBlockModeCSS(config: StyleConfig): string {
  const blockConfig = config.blockConfig || {};
  const gap = blockConfig.gap ?? 1;
  const height = blockConfig.height ?? 40;
  const iconSize = config.iconSize;

  return `
/* ===========================================
   Layout Mode: Block (Text + Icon)
   =========================================== */
.social-share-bar--block {
  display: flex;
  width: 100%;
  gap: ${gap}px;
  border-radius: var(--ss-radius);
  overflow: hidden;
}

/* Block mode button base styles */
.social-share-bar--block .social-share-btn,
.social-share-bar--block a.social-share-btn,
.social-share-bar--block button.social-share-btn {
  height: ${height}px;
  border-radius: 0;
  padding: 0; /* 修正: 移除内边距，完全由 Flex 控制 */
  width: auto;
  min-width: auto;
  flex: 1 1 auto;
  margin: 0;
  border: 0 none;
  outline: 0 none;
  box-shadow: none;
  text-decoration: none;
  justify-content: flex-start; /* 修正: 强制左对齐 */
  position: relative;
}

/* First/last button radius */
.social-share-bar--block .social-share-btn:first-child {
  border-radius: var(--ss-radius) 0 0 var(--ss-radius);
}
.social-share-bar--block .social-share-btn:last-child {
  border-radius: 0 var(--ss-radius) var(--ss-radius) 0;
}
.social-share-bar--block .social-share-btn:only-child {
  border-radius: var(--ss-radius);
}

/* Fixed Square Icon Area - Icon centered in container */
.social-share-bar--block .social-share-icon {
  width: ${height}px;
  height: ${height}px;
  /* Keep sprite size as iconSize, center it in the larger container */
  background-size: ${iconSize}px;
  /* Center icon: offset by (container - icon) / 2 */
  background-position-x: ${(height - iconSize) / 2}px !important;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  z-index: 2;
}

/* Label Centered in Remaining Space - 修正: 文字在剩余空间居中 */
.social-share-label {
  flex-grow: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 14px;
  font-weight: bold;
  color: #fff;
  line-height: ${height}px;
  /* 视觉平衡: 右侧补一点 padding 以平衡左侧图标宽度 */
  padding-right: ${config.iconSize < height ? (height - config.iconSize)/2 : 0}px;
}

/* Icon-Only Mode (P2 / Narrow State) */
.social-share-bar--block .social-share-btn[data-label-state="never"] {
  flex-grow: 0;
  flex-basis: auto;
  width: ${height}px; /* 固定宽度 */
  justify-content: center;
}

.social-share-bar--block .social-share-btn[data-label-state="never"] .social-share-label {
  display: none;
}

/* Tooltip Positioning */
.social-share-bar--block .social-share-tooltip {
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
}
`;
}


/** Generate all CSS for the share bar */
export function generateCSS(
  enabledPlatforms: Platform[],
  config: StyleConfig
): string {
  const bgImageSource = getBgImageSource(config.iconSpritePath);
  
  // Generate CSS for ALL platforms to avoid cache conflicts
  // when multiple instances use different platform subsets
  const allPlatforms = Object.values(allPlatformsMap);
  const platformCSS = allPlatforms
    .map(p => generateButtonCSS(p, config))
    .join('\n');

  return `
/* ===========================================
   Social Share Plugin - Atomic CSS
   =========================================== */
.social-share-bar {
  --ss-icon-size: ${config.iconSize}px;
  --ss-btn-margin: ${config.buttonMargin}px;
  --ss-btn-padding: ${config.buttonPadding}px;
  --ss-z-index: ${config.zIndex};
  --ss-transition: 0.2s ease-out;
  
  position: fixed;
  z-index: var(--ss-z-index);
  display: flex;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  box-sizing: border-box;
}

.social-share-bar *,
.social-share-bar *::before,
.social-share-bar *::after {
  box-sizing: inherit;
}

/* ===========================================
   Position: Left (vertical sidebar)
   Fixed width (icon size), flex-direction: column
   =========================================== */
.social-share-bar--left {
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  flex-direction: column;
  width: auto;
}

/* Left bar gaps - non-square shapes get gap */
.social-share-bar--left.social-share-bar--icon {
  gap: var(--ss-btn-margin);
}

/* ===========================================
   Position: Bottom (horizontal bar)
   width: 100%, flex-direction: row
   =========================================== */
.social-share-bar--bottom {
  bottom: 0;
  left: 0;
  right: 0;
  flex-direction: row;
  width: 100%;
  justify-content: center;
}

/* Bottom bar icon mode - centered icons */
.social-share-bar--bottom.social-share-bar--icon {
  gap: 0;
}

/* Bottom bar block mode - stretch buttons */
.social-share-bar--bottom.social-share-bar--block {
  justify-content: stretch;
}

.social-share-bar--bottom.social-share-bar--block .social-share-btn {
  flex: 1 1 0%;
}

/* ===========================================
   Position: Auto (responsive)
   Mobile-first: starts as bottom, becomes left on desktop
   =========================================== */
.social-share-bar--auto {
  bottom: 0;
  left: 0;
  right: 0;
  flex-direction: row;
  width: 100%;
  justify-content: center;
}

.social-share-bar--auto.social-share-bar--icon {
  gap: 0;
}

/* ===========================================
   Position: Static (inline)
   display: flex, flex-wrap: wrap, width: 100%
   =========================================== */
.social-share-bar--static {
  position: relative;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--ss-btn-margin);
  overflow: visible;
  z-index: auto;
  display: inline-flex;
  width: 100%;
}

/* Static block mode - "Safe Squeeze" single-row layout */
.social-share-bar--static.social-share-bar--block {
  display: flex;
  flex-wrap: nowrap !important; /* CRITICAL: Force single row, never wrap */
  overflow: hidden; /* Clip any potential rendering overflow */
  align-items: center; /* Ensure vertical alignment */
}

.social-share-bar--static.social-share-bar--block .social-share-btn {
  flex: 1 1 auto; /* Grow to fill, shrink if needed */
  min-width: 0; /* CRITICAL: Allow shrinking below text content width */
  max-width: 100%;
  white-space: nowrap; /* Prevent text wrapping inside the button */
}

/* ===========================================
   Shape: Square Overrides
   border-radius: 0, no separators
   Note: gap is NOT forced to 0 here - let block mode control it
   =========================================== */
.social-share-bar--shape-square {
  border-radius: 0 !important;
}

/* Only force gap: 0 for icon mode (non-block), block mode manages its own gap */
.social-share-bar--shape-square.social-share-bar--icon {
  gap: 0 !important;
}

.social-share-bar--shape-square .social-share-btn {
  border-radius: 0 !important;
  margin: 0 !important;
}

/* Square + Bottom: buttons stretch to fill width */
.social-share-bar--shape-square.social-share-bar--bottom .social-share-btn,
.social-share-bar--shape-square.social-share-bar--auto .social-share-btn {
  flex: 1 1 0%;
}

/* Square + Left: stack vertically, no gaps */
.social-share-bar--shape-square.social-share-bar--left {
  gap: 0 !important;
}

/* ===========================================
   Layout Mode: Icon (default)
   =========================================== */
.social-share-bar--icon .social-share-btn,
.social-share-bar--icon a.social-share-btn,
.social-share-bar--icon button.social-share-btn {
  border-radius: var(--ss-radius);
  flex-shrink: 0;
  padding: var(--ss-btn-padding);
  margin: 0;
  border: 0 none;
  outline: 0 none;
  box-shadow: none;
  overflow: visible;
  text-decoration: none;
  -webkit-backface-visibility: hidden;
  backface-visibility: hidden;
  transform: translateZ(0);
}

/* Non-square icon mode in bottom/auto - keep auto width */
.social-share-bar--bottom.social-share-bar--icon:not(.social-share-bar--shape-square) .social-share-btn,
.social-share-bar--auto.social-share-bar--icon:not(.social-share-bar--shape-square) .social-share-btn {
  flex: 0 0 auto;
}

.social-share-bar--icon .social-share-btn:focus,
.social-share-bar--icon .social-share-btn:focus-visible,
.social-share-bar--icon .social-share-btn:active,
.social-share-bar--icon .social-share-btn:hover {
  outline: 0 none;
  box-shadow: none;
  border: 0 none;
  text-decoration: none;
}

/* ===========================================
   Button Base Styles
   =========================================== */
.social-share-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--ss-icon-size);
  min-width: var(--ss-icon-size);
  height: var(--ss-icon-size);
  cursor: pointer;
  text-decoration: none;
  border: none;
  border-radius: 0;
  padding: 0;
  margin: 0;
  transition: background var(--ss-transition);
  position: relative;
  background: none;
  font: inherit;
  color: inherit;
  outline: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
}

/* ===========================================
   Icon Styles
   =========================================== */
.social-share-icon {
  width: var(--ss-icon-size);
  height: var(--ss-icon-size);
  background-image: url('${bgImageSource}');
  background-repeat: no-repeat;
  background-size: var(--ss-icon-size);
  flex-shrink: 0;
}

/* ===========================================
   Tooltip Styles
   =========================================== */
.social-share-tooltip {
  position: absolute;
  white-space: nowrap;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1.4;
  background: #333;
  color: #fff;
  border-radius: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s ease-in-out;
  z-index: 1000;
}

.social-share-bar--bottom .social-share-tooltip,
.social-share-bar--static .social-share-tooltip {
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
}

.social-share-bar--left .social-share-tooltip {
  left: calc(100% + 8px);
  top: 50%;
  transform: translateY(-50%);
}

.social-share-tooltip--visible {
  opacity: 1;
}

.social-share-tooltip-success {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.social-share-tooltip-success svg {
  width: 14px;
  height: 14px;
  fill: currentColor;
}

/* ===========================================
   Platform-specific styles
   =========================================== */
${platformCSS}

/* ===========================================
   Responsive: Auto position switches
   =========================================== */
@media (min-width: ${config.autoWidthThreshold}px) {
  .social-share-bar--auto {
    top: 50%;
    left: 0;
    bottom: auto;
    right: auto;
    transform: translateY(-50%);
    flex-direction: column;
    width: auto;
  }
  
  .social-share-bar--auto.social-share-bar--icon:not(.social-share-bar--shape-square) {
    gap: var(--ss-btn-margin);
  }
  
  .social-share-bar--auto.social-share-bar--icon .social-share-btn {
    flex: 0 0 auto;
  }
  
  /* Square + auto on desktop: vertical stack, no gaps */
  .social-share-bar--auto.social-share-bar--shape-square {
    gap: 0 !important;
  }
  
  .social-share-bar--auto.social-share-bar--shape-square .social-share-btn {
    flex: 0 0 auto;
  }
  
  .social-share-bar--auto .social-share-tooltip {
    left: calc(100% + 8px);
    bottom: auto;
    top: 50%;
    transform: translateY(-50%);
  }
}

@media (max-width: ${config.autoWidthThreshold - 1}px) {
  .social-share-bar--auto {
    bottom: 0;
    left: 0;
    right: 0;
    top: auto;
    transform: none;
    flex-direction: row;
    width: 100%;
    justify-content: center;
  }
  
  .social-share-bar--auto.social-share-bar--icon:not(.social-share-bar--shape-square) {
    gap: 0;
  }
  
  /* Square on mobile bottom: stretch */
  .social-share-bar--auto.social-share-bar--shape-square .social-share-btn {
    flex: 1 1 0%;
  }
  
  .social-share-bar--auto .social-share-tooltip {
    bottom: calc(100% + 8px);
    left: 50%;
    top: auto;
    transform: translateX(-50%);
  }
}

/* ===========================================
   Granular Responsive Breakpoints
   Label collapse based on data-label-state attribute
   Breakpoints: lg(960), md(768), sm(640), xs(480)
   =========================================== */

/* Large Breakpoint: 960px - Collapse "lg" labels */
@media (max-width: 960px) {
  .social-share-bar--block .social-share-btn[data-label-state="lg"] .social-share-label {
    display: none;
  }
  .social-share-bar--block .social-share-btn[data-label-state="lg"] {
    flex-grow: 0;
    flex-basis: auto;
    padding: 0 8px;
  }
}

/* Medium Breakpoint: 768px - Collapse "md" and "lg" labels */
@media (max-width: 768px) {
  .social-share-bar--block .social-share-btn[data-label-state="md"] .social-share-label,
  .social-share-bar--block .social-share-btn[data-label-state="lg"] .social-share-label {
    display: none;
  }
  .social-share-bar--block .social-share-btn[data-label-state="md"],
  .social-share-bar--block .social-share-btn[data-label-state="lg"] {
    flex-grow: 0;
    flex-basis: auto;
    padding: 0 8px;
  }
}

/* Small Breakpoint: 640px - Collapse "sm", "md", and "lg" labels */
@media (max-width: 640px) {
  .social-share-bar--block .social-share-btn[data-label-state="sm"] .social-share-label,
  .social-share-bar--block .social-share-btn[data-label-state="md"] .social-share-label,
  .social-share-bar--block .social-share-btn[data-label-state="lg"] .social-share-label {
    display: none;
  }
  .social-share-bar--block .social-share-btn[data-label-state="sm"],
  .social-share-bar--block .social-share-btn[data-label-state="md"],
  .social-share-bar--block .social-share-btn[data-label-state="lg"] {
    flex-grow: 0;
    flex-basis: auto;
    padding: 0 8px;
  }
}

/* Extra Small Breakpoint: 480px - Collapse "xs", "sm", "md", and "lg" labels */
@media (max-width: 480px) {
  .social-share-bar--block .social-share-btn[data-label-state="xs"] .social-share-label,
  .social-share-bar--block .social-share-btn[data-label-state="sm"] .social-share-label,
  .social-share-bar--block .social-share-btn[data-label-state="md"] .social-share-label,
  .social-share-bar--block .social-share-btn[data-label-state="lg"] .social-share-label {
    display: none;
  }
  .social-share-bar--block .social-share-btn[data-label-state="xs"],
  .social-share-bar--block .social-share-btn[data-label-state="sm"],
  .social-share-bar--block .social-share-btn[data-label-state="md"],
  .social-share-bar--block .social-share-btn[data-label-state="lg"] {
    flex-grow: 0;
    flex-basis: auto;
    padding: 0 8px;
  }
}

${generateBlockModeCSS(config)}
`;
}

/** Create a style element with the generated CSS */
export function createStyleElement(css: string): HTMLStyleElement {
  const style = document.createElement('style');
  style.setAttribute('data-social-share', 'true');
  style.textContent = css;
  return style;
}

/** Inject CSS into document head */
export function injectStyles(css: string, id?: string): HTMLStyleElement {
  if (id) {
    const existing = document.getElementById(id);
    if (existing) {
      return existing as HTMLStyleElement;
    }
  }

  const style = createStyleElement(css);
  if (id) {
    style.id = id;
  }
  document.head.appendChild(style);
  return style;
}

/** Remove injected styles */
export function removeStyles(styleElement: HTMLStyleElement): void {
  styleElement.remove();
}
